/**
 * ExoClick vertical VAST ad loading and tracking.
 *
 * Each video element owns one cached VAST response/tracker for its lifetime.
 * Media URLs are resolved from VAST XML (including wrappers) before they are
 * ever assigned to a <video>.
 */

import { VASTClient, VASTTracker } from "@dailymotion/vast-client";
import { CONFIG } from "./state.js";

const vastClient = new VASTClient();
const adCache = new WeakMap();

const isHttpUrl = (value) => {
  try {
    return ["http:", "https:"].includes(new URL(value).protocol);
  } catch {
    return false;
  }
};

const canPlayMedia = (vid, mediaFile) => {
  if (!mediaFile?.fileURL || !isHttpUrl(mediaFile.fileURL)) return false;
  if (!mediaFile.mimeType) return true;
  return vid.canPlayType(mediaFile.mimeType) !== "";
};

const mediaScore = (mediaFile) => {
  const mimeType = mediaFile.mimeType || "";
  const width = Number(mediaFile.width) || 0;
  const height = Number(mediaFile.height) || 0;
  const vertical = height > width ? 1 : 0;
  const mp4 = mimeType === "video/mp4" ? 1 : 0;
  const progressive = mediaFile.deliveryType === "progressive" ? 1 : 0;
  const pixels = width * height;

  return vertical * 1e12 + mp4 * 1e11 + progressive * 1e10 + pixels;
};

const selectAdAndCreative = (vastResponse, vid) => {
  for (const ad of vastResponse?.ads || []) {
    for (const creative of ad.creatives || []) {
      if (creative.type !== "linear") continue;

      const mediaFile = (creative.mediaFiles || [])
        .filter((candidate) => canPlayMedia(vid, candidate))
        .sort((a, b) => mediaScore(b) - mediaScore(a))[0];

      if (mediaFile) return { ad, creative, mediaFile };
    }
  }

  throw new Error("VAST response did not contain a playable linear creative");
};

const failAd = (vid, message, errorCode) => {
  const state = adCache.get(vid);
  if (!state || state.failed) return;

  state.failed = true;
  vid.dataset.vastStatus = "failed";

  if (state.tracker && errorCode) {
    state.tracker.error({
      ERRORCODE: String(errorCode),
      CONTENTURI: state.mediaUrl || "",
    });
  }

  console.warn(`[VAST] ${message}`);
  queueMicrotask(() => state.onFailure?.(vid));
};

const attachTracker = (state, vid, ad, creative) => {
  const tracker = new VASTTracker(vastClient, ad, creative, null, true);
  state.tracker = tracker;

  tracker.on("clickthrough", (url) => {
    if (isHttpUrl(url)) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  });

  tracker.on("TRACKER-error", (error) => {
    console.warn("[VAST] Tracking error:", error?.message || error);
  });

  vid.dataset.vastVersion = state.vastVersion || "";
};

const getOrCreateState = (vid, onFailure) => {
  let state = adCache.get(vid);
  if (!state) {
    state = {
      promise: null,
      tracker: null,
      mediaUrl: "",
      vastVersion: "",
      failed: false,
      loadedTracked: false,
      impressionTracked: false,
      completionTracked: false,
      onFailure,
    };
    adCache.set(vid, state);
  } else if (onFailure) {
    state.onFailure = onFailure;
  }
  return state;
};

/**
 * Resolve and cache one VAST response for an ad video element.
 * Repeated calls return the same promise and never refetch the tag.
 */
export const loadVastAd = (vid, { onFailure } = {}) => {
  if (!vid) return Promise.reject(new Error("Missing VAST video element"));

  const state = getOrCreateState(vid, onFailure);
  if (state.promise) return state.promise;

  const card = vid.closest(".card");
  card?.classList.add("vast-loading");
  vid.dataset.vastStatus = "loading";

  state.promise = vastClient
    .get(CONFIG.VAST_URL, {
      timeout: CONFIG.VAST_TIMEOUT,
      wrapperLimit: 10,
      resolveAll: false,
      allowMultipleAds: false,
      followAdditionalWrappers: true,
      withCredentials: false,
      fetchOptions: {
        cache: "no-store",
        credentials: "omit",
        referrerPolicy: "strict-origin-when-cross-origin",
      },
    })
    .then((vastResponse) => {
      if (!vastResponse?.ads?.length) {
        throw new Error("VAST returned an empty no-fill response");
      }

      const { ad, creative, mediaFile } = selectAdAndCreative(
        vastResponse,
        vid,
      );
      state.vastVersion = vastResponse.version || "";
      state.mediaUrl = mediaFile.fileURL;
      attachTracker(state, vid, ad, creative);

      // This is the resolved media asset, never the VAST XML URL.
      vid.dataset.src = state.mediaUrl;
      vid.dataset.vastStatus = "ready";
      return state.mediaUrl;
    })
    .catch((error) => {
      failAd(vid, `Ad load failed: ${error.message}`);
      throw error;
    })
    .finally(() => {
      card?.classList.remove("vast-loading");
    });

  return state.promise;
};

export const trackVastLoaded = (vid) => {
  const state = adCache.get(vid);
  if (!state?.tracker || state.loadedTracked || state.failed) return;

  state.loadedTracked = true;
  state.tracker.load();

  const duration =
    Number.isFinite(vid.duration) && vid.duration > 0 ? vid.duration : 0;
  if (duration) state.tracker.setDuration(duration);
};

export const trackVastPlaying = (vid) => {
  const state = adCache.get(vid);
  if (!state?.tracker || state.failed) return;

  if (!state.impressionTracked) {
    state.impressionTracked = true;
    vid.dataset.vastImpressionTracked = "1";
    state.tracker.trackImpression();
  }

  if (!state.completionTracked && vid.currentTime > 0) {
    state.tracker.setProgress(vid.currentTime);
  }
};

export const trackVastProgress = (vid) => {
  const state = adCache.get(vid);
  if (
    !state?.tracker ||
    state.failed ||
    state.completionTracked ||
    !state.impressionTracked
  ) {
    return;
  }

  state.tracker.setProgress(vid.currentTime);
};

export const trackVastComplete = (vid) => {
  const state = adCache.get(vid);
  if (!state?.tracker || state.failed || state.completionTracked) return;

  const duration =
    Number.isFinite(vid.duration) && vid.duration > 0
      ? vid.duration
      : state.tracker.assetDuration;
  if (duration > 0) state.tracker.setProgress(duration);

  state.completionTracked = true;
  vid.dataset.vastCompletionTracked = "1";
  state.tracker.complete();
};

export const trackVastClick = (vid) => {
  const state = adCache.get(vid);
  if (!state?.tracker || state.failed) return false;
  state.tracker.click();
  return true;
};

export const failVastPlayback = (vid, reason = "Media playback failed") => {
  failAd(vid, reason, "405");
};

export const isVastAdVideo = (vid) => vid?.dataset?.vastAd === "1";
